export const SESSION_EXPIRY_EVENT = 'hsSessionExpiry';
export const SESSION_EXPIRY_STORAGE_KEY = 'hs:sessionExpiry';
export const SESSION_EXPIRES_IN_HEADER = 'X-Session-Expires-In';
export const SESSION_WARNING_LEAD_HEADER = 'X-Session-Warning-Lead';

export type SessionExpiry = {
  expiresAt: number;
  warningLeadMs: number;
  receivedAt: number;
};

let latestSessionExpiry: SessionExpiry | null = null;

function parseSeconds(value: string | null) {
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }
  const seconds = Number(value);
  return seconds > 0 ? seconds : null;
}

function isSessionExpiry(value: unknown): value is SessionExpiry {
  const expiry = value as Partial<SessionExpiry> | null;
  return (
    Number.isFinite(expiry?.expiresAt) &&
    Number.isFinite(expiry?.warningLeadMs) &&
    Number.isFinite(expiry?.receivedAt)
  );
}

function readStoredSessionExpiry(): SessionExpiry | null {
  try {
    const stored: unknown = JSON.parse(
      window.localStorage.getItem(SESSION_EXPIRY_STORAGE_KEY) ?? 'null',
    );
    return isSessionExpiry(stored) ? stored : null;
  } catch {
    // Storage is blocked, e.g. by private browsing or disabled site data, or
    // holds something that isn't ours.
    return null;
  }
}

// Other tabs share the session cookie, so whichever tab heard from the server
// last knows when the session ends, even when that is sooner than before.
export function getSessionExpiry(): SessionExpiry | null {
  const stored = readStoredSessionExpiry();
  if (!latestSessionExpiry || !stored) {
    return latestSessionExpiry ?? stored;
  }
  return stored.receivedAt > latestSessionExpiry.receivedAt
    ? stored
    : latestSessionExpiry;
}

export function recordSessionExpiry(headers: Headers) {
  const expiresInSeconds = parseSeconds(headers.get(SESSION_EXPIRES_IN_HEADER));
  const warningLeadSeconds = parseSeconds(
    headers.get(SESSION_WARNING_LEAD_HEADER),
  );
  if (expiresInSeconds === null || warningLeadSeconds === null) {
    return;
  }

  const now = Date.now();
  latestSessionExpiry = {
    expiresAt: now + expiresInSeconds * 1000,
    warningLeadMs: warningLeadSeconds * 1000,
    receivedAt: now,
  };
  try {
    window.localStorage.setItem(
      SESSION_EXPIRY_STORAGE_KEY,
      JSON.stringify(latestSessionExpiry),
    );
  } catch {
    // Blocked storage or a full quota: only this tab learns the new expiry.
  }
  window.dispatchEvent(new Event(SESSION_EXPIRY_EVENT));
}

export function subscribeToSessionExpiry(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === SESSION_EXPIRY_STORAGE_KEY) {
      onChange();
    }
  };
  window.addEventListener(SESSION_EXPIRY_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(SESSION_EXPIRY_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function resetSessionExpiryForTests() {
  latestSessionExpiry = null;
}
